import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Form, Row, Col } from "react-bootstrap";
import Select from "react-select";
import validate from "validate.js";
import { createOrUpdateBrand, fetchPrograms } from "actions"; // adjust import paths

const schema = {
  brand_name: {
    presence: { allowEmpty: false, message: "^Brand name is required" },
  },
  program_id: {
    presence: { allowEmpty: false, message: "^Program is required" },
  },
};

const BrandAddUpdateModal = ({
  show,
  handleClose,
  isEdit = false,
  object_viewed_id,
}) => {
  const dispatch = useDispatch();
  const brandState = useSelector((s) => s.brandState);
  const programState = useSelector((s) => s.programState);

  const { brandRecord, validation_error, redirectToList } = brandState;

  const [formState, setFormState] = useState({
    isValid: false,
    values: {},
    touched: {},
    errors: {},
  });

  const [programOptions, setProgramOptions] = useState([]);

  // Fetch all programs for dropdown
  useEffect(() => {
    if (object_viewed_id) {
      dispatch(fetchPrograms({ object_viewed_id }));
    }
  }, [dispatch, object_viewed_id]);

  // Convert fetched programs to react-select options
  useEffect(() => {
    if (programState.programsList?.length) {
      const options = programState.programsList.map((p) => ({
        value: p.id,
        label: p.program_name,
      }));
      setProgramOptions(options);
    }
  }, [programState.programsList]);

  // Prepopulate when editing
  useEffect(() => {
    if (isEdit && brandRecord) {
      const values = {
        id: brandRecord.id,
        brand_name: brandRecord.brand_name || "",
        program_id: brandRecord.program_id || "",
      };

      setFormState((fs) => ({
        ...fs,
        values,
      }));
    }
  }, [brandRecord, isEdit]);

  // Watch for backend validation errors
  useEffect(() => {
    if (validation_error) {
      setFormState((fs) => ({
        ...fs,
        isValid: false,
        errors: validation_error || {},
      }));
    }
  }, [validation_error]);

  // Local validation with validate.js
  useEffect(() => {
    const errors = validate(formState.values, schema);
    setFormState((fs) => ({
      ...fs,
      isValid: !errors,
      errors: errors || {},
    }));
  }, [formState.values]);

  // Handle redirect to list after successful submit
  useEffect(() => {
    if (redirectToList) {
      handleClose();
    }
  }, [redirectToList]);

  // Input handlers
  const handleChange = (e) => {
    e.persist();
    setFormState((fs) => ({
      ...fs,
      values: { ...fs.values, [e.target.name]: e.target.value },
      touched: { ...fs.touched, [e.target.name]: true },
    }));
  };

  const handleProgramChange = (selected) => {
    setFormState((fs) => ({
      ...fs,
      values: { ...fs.values, program_id: selected ? selected.value : "" },
      touched: { ...fs.touched, program_id: true },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const method = formState.values.id ? "PUT" : "POST";
    dispatch(createOrUpdateBrand(formState.values, method, object_viewed_id));
  };

  const hasError = (field) =>
    formState.touched[field] && formState.errors[field];

  const onModelClose = () => {
    setFormState({
      isValid: false,
      values: {},
      touched: {},
      errors: {},
    });
    dispatch({ type: "RESET_BRAND_RECORD" });
    handleClose();
  };

  return (
    <Modal show={show} onHide={onModelClose} centered>
      <div className="p-2">
        <Modal.Header className="p-2" closeButton>
          <Modal.Title>
            <h5 className="font-20 font-weight-bold heading-color">
              {isEdit ? "Update Brand" : "Add New Brand"}
            </h5>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col lg={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Brand Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="brand_name"
                    placeholder="Enter brand name"
                    className={`input-field ${
                      hasError("brand_name") ? "is-invalid" : ""
                    }`}
                    onChange={handleChange}
                    value={formState.values.brand_name || ""}
                  />
                  {hasError("brand_name") && (
                    <Form.Control.Feedback type="invalid">
                      {formState.errors.brand_name[0] ||
                        formState.errors.brand_name}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>

              <Col lg={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Program</Form.Label>
                  <Select
                    options={programOptions}
                    placeholder="Select Program"
                    className={`multi-select w-100 ${
                      hasError("program_id") ? "is-invalid" : ""
                    }`}
                    classNamePrefix="react-select"
                    value={
                      programOptions.find(
                        (opt) => opt.value === formState.values.program_id
                      ) || null
                    }
                    onChange={handleProgramChange}
                    isSearchable
                  />
                  {hasError("program_id") && (
                    <div className="invalid-feedback d-block">
                      {formState.errors.program_id[0] ||
                        formState.errors.program_id}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col lg={6}>
                <button
                  type="button"
                  className="c-b-btn w-100"
                  onClick={onModelClose}
                >
                  Cancel
                </button>
              </Col>
              <Col lg={6}>
                <button
                  type="submit"
                  className="n-u-btn w-100"
                  disabled={!formState.isValid}
                >
                  {isEdit ? "Update" : "Add"}
                </button>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
      </div>
    </Modal>
  );
};

export default BrandAddUpdateModal;
